/**
 * DetailedModeForm Component
 *
 * Multi-step wizard for complex requirements in FORGE
 * Step 1: Type Selection
 * Step 2: Details Form
 * Step 3: Review & Submit
 */

import React, { useState, useEffect, useRef } from 'react';
import { useForge } from '../hooks/useForge';
import { TypeSelector } from './TypeSelector';
import { ReviewStep } from './ReviewStep';
import type { DetailedModeFormProps, RequirementType, DetailedModeStep, DetailedFormData } from '../types/forge';
import styles from '../styles/forge.module.css';

const DEFAULT_FORM_DATA: DetailedFormData = {
  title: '',
  description: '',
  requirementType: 'feature',
  businessValue: '',
  technicalDetails: '',
  acceptanceCriteria: '',
  dependencies: '',
  priority: 'medium',
};

export const DetailedModeForm: React.FC<DetailedModeFormProps> = ({
  onSubmit,
  isSubmitting,
  error,
  serviceId,
}) => {
  const forge = useForge();
  const draft = forge.detailedDraft;

  // Initialize from draft or defaults
  const [currentStep, setCurrentStep] = useState<DetailedModeStep>(draft?.step || 'type');
  const [selectedType, setSelectedType] = useState<RequirementType | null>(draft?.selectedType || null);
  const [formData, setFormData] = useState<DetailedFormData>(draft?.formData || { ...DEFAULT_FORM_DATA });

  // Track whether this is the initial mount to avoid overwriting context on first render
  const isInitialMount = useRef(true);

  // Sync local state back to context draft on every change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    forge.setDetailedDraft({
      step: currentStep,
      selectedType,
      formData,
    });
  }, [currentStep, selectedType, formData]);

  const updateStep = (newStep: DetailedModeStep) => {
    setCurrentStep(newStep);
  };

  // Handle type selection and advance to details
  const handleTypeSelect = (type: RequirementType) => {
    setSelectedType(type);
    setFormData((prev) => ({ ...prev, requirementType: type }));
    updateStep('details');
  };

  // Handle form field changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle advancing from details to review
  const handleDetailsNext = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.description.trim()) {
      return;
    }

    updateStep('review');
  };

  // Handle navigation back to a specific step
  const handleEdit = (step: DetailedModeStep) => {
    updateStep(step);
  };

  // Handle final submission from review step
  const handleFinalSubmit = async () => {
    if (!selectedType) return;

    try {
      await onSubmit({
        title: formData.title,
        description: formData.description,
        requirementType: formData.requirementType,
        questionnaireData: {
          businessValue: formData.businessValue,
          technicalDetails: formData.technicalDetails,
          acceptanceCriteria: formData.acceptanceCriteria,
          dependencies: formData.dependencies,
          priority: formData.priority,
        },
        pageContext: {
          url: window.location.href,
          title: document.title,
          timestamp: new Date().toISOString(),
          serviceId,
        },
      });

      // Clear draft after successful submission
      forge.resetDraft();
    } catch (err) {
      console.error('Failed to submit detailed form:', err);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isSubmitting) return;

      // Escape to go back
      if (e.key === 'Escape' && currentStep !== 'type') {
        e.preventDefault();
        if (currentStep === 'review') {
          updateStep('details');
        } else if (currentStep === 'details') {
          updateStep('type');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, isSubmitting]);

  return (
    <div className={styles.detailedModeForm}>
      {/* Step 1: Type Selection */}
      {currentStep === 'type' && (
        <TypeSelector selectedType={selectedType} onSelect={handleTypeSelect} />
      )}

      {/* Step 2: Details Form */}
      {currentStep === 'details' && (
        <form onSubmit={handleDetailsNext} className={styles.form}>
          <div className={styles.formSection}>
            <h3>Basic Information</h3>

            <div className={styles.formField}>
              <label htmlFor="title" className={styles.formLabel}>
                Title <span className={styles.formRequired}>*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Brief summary of the requirement"
                className={styles.formInput}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formField}>
              <label htmlFor="description" className={styles.formLabel}>
                Description <span className={styles.formRequired}>*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Detailed description of what needs to be built"
                className={styles.formTextarea}
                rows={4}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className={styles.formSection}>
            <h3>Details</h3>

            <div className={styles.formField}>
              <label htmlFor="businessValue" className={styles.formLabel}>
                Business Value
              </label>
              <textarea
                id="businessValue"
                name="businessValue"
                value={formData.businessValue}
                onChange={handleChange}
                placeholder="Why is this important? What value does it provide?"
                className={styles.formTextarea}
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formField}>
              <label htmlFor="technicalDetails" className={styles.formLabel}>
                Technical Details
              </label>
              <textarea
                id="technicalDetails"
                name="technicalDetails"
                value={formData.technicalDetails}
                onChange={handleChange}
                placeholder="Technical implementation details, architecture considerations"
                className={styles.formTextarea}
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formField}>
              <label htmlFor="acceptanceCriteria" className={styles.formLabel}>
                Acceptance Criteria
              </label>
              <textarea
                id="acceptanceCriteria"
                name="acceptanceCriteria"
                value={formData.acceptanceCriteria}
                onChange={handleChange}
                placeholder="What defines success? How will we know it's done?"
                className={styles.formTextarea}
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formField}>
              <label htmlFor="dependencies" className={styles.formLabel}>
                Dependencies
              </label>
              <textarea
                id="dependencies"
                name="dependencies"
                value={formData.dependencies}
                onChange={handleChange}
                placeholder="External dependencies, prerequisites, blocking issues"
                className={styles.formTextarea}
                rows={2}
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formField}>
              <label htmlFor="priority" className={styles.formLabel}>
                Priority
              </label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className={styles.formSelect}
                disabled={isSubmitting}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          {error && (
            <div className={styles.formError}>
              <p>Error: {error}</p>
            </div>
          )}

          <div className={styles.formActions}>
            <button
              type="button"
              onClick={() => updateStep('type')}
              className={`${styles.button} ${styles.buttonSecondary}`}
              disabled={isSubmitting}
            >
              Back
            </button>
            <button
              type="submit"
              className={`${styles.button} ${styles.buttonPrimary}`}
              disabled={!formData.title.trim() || !formData.description.trim() || isSubmitting}
            >
              Next: Review
            </button>
          </div>
        </form>
      )}

      {/* Step 3: Review & Submit */}
      {currentStep === 'review' && selectedType && (
        <ReviewStep
          formData={formData}
          requirementType={selectedType}
          serviceId={serviceId}
          onEdit={handleEdit}
          onSubmit={handleFinalSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};

export default DetailedModeForm;
